import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import React from 'react';
import classNames from 'classnames';
import { getThemeClassNames } from '@cognite/cogs-utils';

/**
 * Divider
 * =============================================================================
 * The Divider component is used to create a visual separation between elements
 * in a layout. It can be used to visually group related content or to divide a
 * page into sections.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-divider--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Divider direction="horizontal" weight="1px" endcap="round">
 *   My horizontal divider with a round endcap
 * </Divider>
 *
 * <Divider direction="vertical" weight="2px" endcap="square">
 *   My vertical divider with a square endcap
 * </Divider>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param direction - The direction of the divider.
 * @param endcap - The endcap of the divider.
 * @param inverted - Whether to optimize the divider for use on a dark surface.
 * @param weight - The "weight" or thickness of the divider.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-divider--base)
 */
var Divider = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var _a$direction = _a.direction,
    direction = _a$direction === undefined ? 'horizontal' : _a$direction,
    _a$weight = _a.weight,
    weight = _a$weight === undefined ? '1px' : _a$weight,
    _a$endcap = _a.endcap,
    endcap = _a$endcap === undefined ? 'square' : _a$endcap,
    _a$spacing = _a.spacing,
    spacing = _a$spacing === undefined ? '0px' : _a$spacing,
    inverted = _a.inverted,
    _a$length = _a.length,
    length = _a$length === undefined ? '100%' : _a$length,
    className = _a.className,
    style = _a.style,
    themeProps = _a.themeProps,
    otherProps = __rest(_a, ["direction", "weight", "endcap", "spacing", "inverted", "length", "className", "style", "themeProps"]);
  return jsx("hr", Object.assign({
    ref: ref,
    "aria-orientation": direction,
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-divider', "cogs-divider--weight-" + weight, "cogs-divider--direction-" + direction, "cogs-divider--endcap-" + endcap].concat(getThemeClassNames(inverted, themeProps), [className])),
    style: Object.assign({
      '--Divider-length': length,
      '--Divider-spacing': spacing
    }, style)
  }, otherProps));
});

export { Divider };
